import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Filter, CheckCircle, XCircle, Clock, DollarSign, AlertTriangle, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import api, { formatApiError } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const statusColors = {
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  deposit_paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  fully_paid: 'bg-green-100 text-green-800 border-green-200',
  expired: 'bg-gray-100 text-gray-600 border-gray-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-red-50 text-red-600 border-red-100',
};

const statusLabels = {
  pending_approval: 'Pendiente',
  approved: 'Aprobada',
  deposit_paid: 'Apartado Pagado',
  fully_paid: 'Pagada Total',
  expired: 'Expirada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada',
};

function TimeRemaining({ expiresAt, isExpired }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!expiresAt || isExpired) return;
    const update = () => {
      const now = new Date();
      const exp = new Date(expiresAt.replace('Z', '+00:00'));
      const diff = exp - now;
      if (diff <= 0) {
        setTimeLeft('Expirado');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, isExpired]);

  if (!expiresAt) return null;
  const isExpiredNow = timeLeft === 'Expirado';

  return (
    <span className={`text-xs font-mono ${isExpiredNow ? 'text-red-600' : 'text-blue-700'}`}>
      {isExpiredNow ? 'Tiempo agotado' : timeLeft}
    </span>
  );
}

export default function AdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment confirmation modal
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentReservation, setPaymentReservation] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Reject modal
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReservation, setRejectReservation] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/admin/reservations${params}`);
      setReservations(response.data);
    } catch (error) {
      toast.error('Error al cargar reservaciones');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchReservations, 30000);
    return () => clearInterval(interval);
  }, [fetchReservations]);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/reservations/${id}/approve`);
      toast.success('Reservacion aprobada. El cliente tiene 4 horas para pagar.');
      fetchReservations();
      setSelectedReservation(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (reservation) => {
    setRejectReservation(reservation);
    setRejectReason('');
    setRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReservation) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/reservations/${rejectReservation.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Reservacion rechazada');
      fetchReservations();
      setSelectedReservation(null);
      setRejectModal(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const openPaymentModal = (reservation) => {
    setPaymentReservation(reservation);
    setPaymentAmount(String(reservation.deposit_amount || ''));
    setPaymentNotes('');
    setPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentReservation || !paymentAmount) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/reservations/${paymentReservation.id}/confirm-payment`, {
        amount_paid: parseFloat(paymentAmount),
        payment_notes: paymentNotes || null,
      });
      toast.success(res.data.message);
      fetchReservations();
      setSelectedReservation(null);
      setPaymentModal(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExpire = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/reservations/${id}/expire`);
      toast.success('Reservacion marcada como expirada');
      fetchReservations();
      setSelectedReservation(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredReservations = reservations.filter((r) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      r.user_name?.toLowerCase().includes(s) ||
      r.user_email?.toLowerCase().includes(s) ||
      r.id?.toLowerCase().includes(s)
    );
  });

  const pendingCount = reservations.filter((r) => r.status === 'pending_approval').length;
  const approvedCount = reservations.filter((r) => r.status === 'approved').length;

  return (
    <div data-testid="admin-reservations">
      <h1 className="font-serif text-3xl text-[#1A1A1A] mb-6">Reservaciones</h1>

      {/* Quick Stats */}
      {(pendingCount > 0 || approvedCount > 0) && (
        <div className="flex flex-wrap gap-3 mb-6">
          {pendingCount > 0 && (
            <button
              onClick={() => setStatusFilter('pending_approval')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              data-testid="filter-pending-btn"
            >
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">{pendingCount} por aprobar</span>
            </button>
          )}
          {approvedCount > 0 && (
            <button
              onClick={() => setStatusFilter('approved')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              data-testid="filter-approved-btn"
            >
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">{approvedCount} esperando pago</span>
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, email o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="reservation-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-56" data-testid="status-filter-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending_approval">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobadas</SelectItem>
                <SelectItem value="deposit_paid">Apartado Pagado</SelectItem>
                <SelectItem value="fully_paid">Pagadas Total</SelectItem>
                <SelectItem value="expired">Expiradas</SelectItem>
                <SelectItem value="rejected">Rechazadas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reservations List */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#5C4033]" />
            </div>
          ) : filteredReservations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No se encontraron reservaciones
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Huesped</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Fechas</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Total</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Pagado</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Estado</th>
                    <th className="text-right p-4 font-medium text-gray-600 text-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredReservations.map((r) => {
                    const isApprovedExpired = r.status === 'approved' && r.is_expired;
                    return (
                      <tr key={r.id} className={`hover:bg-gray-50 ${isApprovedExpired ? 'bg-red-50/50' : ''}`}>
                        <td className="p-4">
                          <p className="font-medium text-sm">{r.user_name}</p>
                          <p className="text-xs text-gray-500">{r.user_email}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm">{formatDate(r.check_in)}</p>
                          {r.check_out && (
                            <p className="text-xs text-gray-500">al {formatDate(r.check_out)}</p>
                          )}
                        </td>
                        <td className="p-4 font-serif text-sm">{formatCurrency(r.total)}</td>
                        <td className="p-4">
                          <p className="text-sm font-medium">{formatCurrency(r.amount_paid || 0)}</p>
                          {r.amount_remaining > 0 && r.status !== 'expired' && r.status !== 'rejected' && (
                            <p className="text-xs text-amber-600">Resta: {formatCurrency(r.amount_remaining)}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge className={`text-xs ${statusColors[r.status] || 'bg-gray-100 text-gray-600'}`}>
                            {statusLabels[r.status] || r.status}
                          </Badge>
                          {r.status === 'approved' && r.expires_at && (
                            <div className="mt-1">
                              <TimeRemaining expiresAt={r.expires_at} isExpired={r.is_expired} />
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedReservation(r)}
                            data-testid={`view-reservation-${r.id}`}
                          >
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Detalle de Reservacion</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Huesped</p>
                  <p className="font-medium">{selectedReservation.user_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium text-xs break-all">{selectedReservation.user_email}</p>
                </div>
                {selectedReservation.user_phone && (
                  <div>
                    <p className="text-gray-500">Telefono</p>
                    <p className="font-medium">{selectedReservation.user_phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Tipo</p>
                  <p className="font-medium">{selectedReservation.reservation_type === 'hospedaje' ? 'Hospedaje' : 'Renta de Dia'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Check-in</p>
                  <p className="font-medium">{formatDate(selectedReservation.check_in)}</p>
                </div>
                {selectedReservation.check_out && (
                  <div>
                    <p className="text-gray-500">Check-out</p>
                    <p className="font-medium">{formatDate(selectedReservation.check_out)}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Huespedes</p>
                  <p className="font-medium">{selectedReservation.guests}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mascotas</p>
                  <p className="font-medium">{selectedReservation.pets}</p>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(selectedReservation.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Limpieza</span>
                  <span>{formatCurrency(selectedReservation.cleaning_fee)}</span>
                </div>
                {selectedReservation.pet_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mascotas</span>
                    <span>{formatCurrency(selectedReservation.pet_fee)}</span>
                  </div>
                )}
                {selectedReservation.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Descuento</span>
                    <span>-{formatCurrency(selectedReservation.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-serif text-base border-t pt-2">
                  <span>Total</span>
                  <span className="text-[#5C4033]">{formatCurrency(selectedReservation.total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Apartado sugerido</span>
                  <span className="text-[#C8962D]">{formatCurrency(selectedReservation.deposit_amount)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium">
                  <span>Pagado</span>
                  <span className="text-green-700">{formatCurrency(selectedReservation.amount_paid || 0)}</span>
                </div>
                {selectedReservation.amount_remaining > 0 && (
                  <div className="flex justify-between text-sm font-medium">
                    <span>Restante</span>
                    <span className="text-amber-700">{formatCurrency(selectedReservation.amount_remaining)}</span>
                  </div>
                )}
              </div>

              {/* Timer for approved */}
              {selectedReservation.status === 'approved' && selectedReservation.expires_at && (
                <div className={`p-3 rounded-lg border flex items-center gap-3 ${selectedReservation.is_expired ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                  <Clock className={`h-5 w-5 ${selectedReservation.is_expired ? 'text-red-500' : 'text-blue-500'}`} />
                  <div>
                    <p className={`text-sm font-medium ${selectedReservation.is_expired ? 'text-red-700' : 'text-blue-700'}`}>
                      {selectedReservation.is_expired ? 'Tiempo de pago agotado' : 'Esperando pago del cliente'}
                    </p>
                    <TimeRemaining expiresAt={selectedReservation.expires_at} isExpired={selectedReservation.is_expired} />
                  </div>
                </div>
              )}

              {selectedReservation.special_requests && (
                <div>
                  <p className="text-gray-500 text-sm">Peticiones especiales</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">{selectedReservation.special_requests}</p>
                </div>
              )}

              {/* Payment History */}
              {selectedReservation.payment_history?.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Historial de pagos</p>
                  <div className="space-y-2">
                    {selectedReservation.payment_history.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-green-50 p-3 rounded-lg text-sm">
                        <div>
                          <p className="font-medium text-green-800">{formatCurrency(p.amount)}</p>
                          {p.notes && <p className="text-xs text-green-600">{p.notes}</p>}
                        </div>
                        <p className="text-xs text-green-600">{formatDate(p.date)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="border-t pt-4 space-y-3">
                {/* Pending Approval -> Approve or Reject */}
                {selectedReservation.status === 'pending_approval' && (
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-[#3D5A3E] hover:bg-[#3D5A3E]/90 text-white"
                      disabled={actionLoading}
                      onClick={() => handleApprove(selectedReservation.id)}
                      data-testid="approve-reservation-btn"
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Aprobar
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      disabled={actionLoading}
                      onClick={() => openRejectModal(selectedReservation)}
                      data-testid="reject-reservation-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                )}

                {/* Approved (not expired) -> Confirm Payment */}
                {selectedReservation.status === 'approved' && !selectedReservation.is_expired && (
                  <Button
                    className="w-full bg-[#C8962D] hover:bg-[#C8962D]/90 text-white"
                    disabled={actionLoading}
                    onClick={() => openPaymentModal(selectedReservation)}
                    data-testid="confirm-payment-btn"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Confirmar Pago
                  </Button>
                )}

                {/* Approved expired -> Expire */}
                {selectedReservation.status === 'approved' && selectedReservation.is_expired && (
                  <Button
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    disabled={actionLoading}
                    onClick={() => handleExpire(selectedReservation.id)}
                    data-testid="expire-reservation-btn"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Marcar como Expirada
                  </Button>
                )}

                {/* Deposit paid -> Confirm remaining payment */}
                {selectedReservation.status === 'deposit_paid' && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    disabled={actionLoading}
                    onClick={() => openPaymentModal(selectedReservation)}
                    data-testid="confirm-remaining-payment-btn"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Registrar Pago Restante ({formatCurrency(selectedReservation.amount_remaining)})
                  </Button>
                )}
              </div>

              <p className="text-xs text-gray-400 mt-2">ID: {selectedReservation.id}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Modal */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">Confirmar Pago</DialogTitle>
          </DialogHeader>
          {paymentReservation && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><span className="text-gray-500">Cliente:</span> {paymentReservation.user_name}</p>
                <p><span className="text-gray-500">Total:</span> {formatCurrency(paymentReservation.total)}</p>
                <p><span className="text-gray-500">Ya pagado:</span> {formatCurrency(paymentReservation.amount_paid || 0)}</p>
                <p className="font-medium"><span className="text-gray-500">Pendiente:</span> {formatCurrency(paymentReservation.amount_remaining)}</p>
              </div>
              <div>
                <Label>Monto recibido (MXN)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Ej: 5000"
                  className="mt-1"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Ej: Transferencia BBVA ref. 12345"
                  className="mt-1"
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModal(false)}>Cancelar</Button>
            <Button
              className="bg-[#C8962D] hover:bg-[#C8962D]/90 text-white"
              disabled={actionLoading || !paymentAmount || parseFloat(paymentAmount) <= 0}
              onClick={handleConfirmPayment}
              data-testid="submit-payment-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirmar {paymentAmount ? formatCurrency(parseFloat(paymentAmount)) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal} onOpenChange={setRejectModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">Rechazar Reservacion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Se notificara al cliente que su solicitud fue rechazada.
            </p>
            <div>
              <Label>Razon (opcional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej: Fechas no disponibles por mantenimiento"
                className="mt-1"
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModal(false)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={actionLoading}
              onClick={handleReject}
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
